import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import Header from '../../../components/layout/Header';
import Sidebar from '../../../components/layout/Sidebar';
import ProductionBarByMonth from '../../../components/analytics/production/ProductionBarByMonth';
import Loader from '../../../components/layout/Loader';
import { API_URI } from '../../../constants/GlobalConstants';
import {
  Tabs,
  Tab,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Paper,
  Chip,
  useTheme,
  alpha,
  Breadcrumbs,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  PieChart,
  BarChart,
  Home,
  Timeline
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ProductionAnalytics = ({ nav }) => {
  const [productionData, setProductionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();

  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const navigate = useNavigate();

  useEffect(() => {
    if (!userInfo) {
      navigate('/sign-in');
    } else {
      fetchProductionData();
    }
    window.scrollTo(0, 0);
  }, [userInfo, navigate]);

  const fetchProductionData = async () => {
    try {
      const { data } = await axios.get(`${API_URI}/api/analytics/production-data`, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setProductionData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching production data:', error);
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Chart configurations with dark mode support
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
          font: {
            size: 12,
            family: 'Roboto, sans-serif'
          }
        }
      },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#ffffff',
        titleColor: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
        bodyColor: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
        borderColor: theme.palette.primary.main,
        borderWidth: 1
      }
    },
    scales: {
      x: {
        ticks: {
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333'
        },
        grid: {
          color: theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'
        }
      },
      y: {
        ticks: {
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333'
        },
        grid: {
          color: theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0'
        }
      }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
          font: {
            size: 12,
            family: 'Roboto, sans-serif'
          },
          padding: 20,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#ffffff',
        titleColor: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
        bodyColor: theme.palette.mode === 'dark' ? '#ffffff' : '#333333',
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
        callbacks: {
          label: function (tooltipItem) {
            const label = tooltipItem.label || '';
            const value = tooltipItem.raw || 0;
            return `${label}: ${value} orders`;
          },
        },
      }
    }
  };

  // Existing Dealer-wise Orders Chart
  const dealerWiseData = {
    labels: Object.keys(productionData?.dealerWiseData || {}),
    datasets: [
      {
        label: 'Order Count',
        data: Object.values(productionData?.dealerWiseData || {}).map((data) => data.orderCount),
        backgroundColor: alpha(theme.palette.primary.main, 0.7),
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'Product Count',
        data: Object.values(productionData?.dealerWiseData || {}).map((data) => data.productCount),
        backgroundColor: alpha(theme.palette.secondary.main, 0.7),
        borderColor: theme.palette.secondary.main,
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  // Product Type Order Count Doughnut Chart
  const productOrderCountData = {
    labels: Object.keys(productionData?.productOrderCount || {}),
    datasets: [
      {
        data: Object.values(productionData?.productOrderCount || {}),
        backgroundColor: [
          '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD',
          '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
        ],
        borderWidth: 3,
        borderColor: theme.palette.background.paper,
        hoverOffset: 8,
      },
    ],
  };

  // Order Status Distribution Doughnut Chart
  const orderStatusData = {
    labels: Object.keys(productionData?.orderStatusCount || {}),
    datasets: [
      {
        data: Object.values(productionData?.orderStatusCount || {}),
        backgroundColor: [
          '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#fd7e14'
        ],
        borderWidth: 3,
        borderColor: theme.palette.background.paper,
        hoverOffset: 8,
      },
    ],
  };

  // Dealer Revenue Trends Line Chart
  const dealerRevenueTrendsData = {
    labels: productionData?.dealerRevenueTrends
      ? Object.keys(productionData.dealerRevenueTrends[Object.keys(productionData.dealerRevenueTrends)[0]] || {})
      : [],
    datasets: Object.keys(productionData?.dealerRevenueTrends || {}).map((dealer, index) => {
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];
      return {
        label: dealer,
        data: Object.values(productionData?.dealerRevenueTrends[dealer] || {}),
        fill: false,
        borderColor: colors[index % colors.length],
        backgroundColor: alpha(colors[index % colors.length], 0.1),
        tension: 0.4,
        borderWidth: 3,
        pointBackgroundColor: colors[index % colors.length],
        pointBorderColor: '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      };
    }),
  };

  // Revenue Distribution by Dealer Bar Chart
  const revenueDistributionData = {
    labels: Object.keys(productionData?.dealerWiseData || {}),
    datasets: [
      {
        label: 'Revenue ($)',
        data: Object.values(productionData?.dealerWiseData || {}).map((data) => data.revenue),
        backgroundColor: alpha('#FF6B6B', 0.7),
        borderColor: '#FF6B6B',
        borderWidth: 2,
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card 
      elevation={3}
      sx={{ 
        height: '100%',
        background: theme.palette.mode === 'dark' 
          ? `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`
          : `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.05)} 0%, ${alpha(theme.palette[color].main, 0.02)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography 
              variant="h4" 
              component="div" 
              fontWeight="bold"
              className="text-white"
              sx={{ 
                mb: 1,
                color: 'white !important'
              }}
            >
              {value || 'N/A'}
            </Typography>
            <Typography 
              variant="body2" 
              className="text-white-50"
              fontWeight="medium"
              sx={{ 
                color: 'rgba(255,255,255,0.7) !important'
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette[color].main, 0.1),
              color: theme.palette[color].main,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const ChartCard = ({ title, children, icon }) => (
    <Card 
      elevation={2}
      sx={{ 
        mb: 4,
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
        border: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'}`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Box
            sx={{
              p: 1,
              borderRadius: '8px',
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography 
            variant="h6" 
            fontWeight="bold"
            className="text-white"
            sx={{ 
              color: 'white !important'
            }}
          >
            {title}
          </Typography>
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <div className={nav.isSidebar}>
      <Header nav={nav} />
      <Sidebar nav={nav} />
      <main className="page-content">
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Breadcrumb */}
          <Box sx={{ mb: 3 }}>
            <Breadcrumbs 
              aria-label="breadcrumb"
              className="text-white"
              sx={{ 
                color: 'white !important',
                '& .MuiBreadcrumbs-separator': {
                  color: 'rgba(255,255,255,0.7) !important'
                }
              }}
            >
              <Link 
                to="/" 
                className="text-white-50"
                style={{ 
                  color: 'rgba(255,255,255,0.7) !important',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Home sx={{ mr: 0.5, fontSize: 20 }} />
                Dashboard
              </Link>
              <Typography 
                className="text-white"
                sx={{ 
                  color: 'white !important',
                  display: 'flex', 
                  alignItems: 'center' 
                }}
              >
                <Assessment sx={{ mr: 0.5, fontSize: 20 }} />
                Production Analytics
              </Typography>
            </Breadcrumbs>
          </Box>

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              fontWeight="bold"
              className="text-white"
              sx={{ 
                mb: 1,
                color: 'white !important'
              }}
            >
              Production Analytics
            </Typography>
            <Typography 
              variant="body1" 
              className="text-white-50"
              sx={{ 
                color: 'rgba(255,255,255,0.7) !important'
              }}
            >
              Comprehensive insights for the last 6 months
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <Loader />
            </Box>
          ) : (
            <>
              {/* Key Metrics Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <StatCard
                    title="Most Ordered Product"
                    value={productionData?.mostOrderedProduct}
                    icon={<TrendingUp fontSize="large" />}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <StatCard
                    title="Top Dealer by Revenue"
                    value={productionData?.topDealer}
                    icon={<Assessment fontSize="large" />}
                    color="secondary"
                  />
                </Grid>
              </Grid>

              {/* Tabs */}
              <Paper 
                elevation={1}
                sx={{ 
                  mb: 4,
                  backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
                  border: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'}`,
                }}
              >
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{
                    '& .MuiTab-root': {
                      color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      py: 2,
                    },
                    '& .Mui-selected': {
                      color: theme.palette.primary.main,
                    },
                    '& .MuiTabs-indicator': {
                      height: 3,
                      borderRadius: '2px 2px 0 0',
                    },
                  }}
                >
                  <Tab 
                    label="Dealer & Product Analytics" 
                    icon={<BarChart />}
                    iconPosition="start"
                  />
                  <Tab 
                    label="Order Status & Revenue" 
                    icon={<PieChart />}
                    iconPosition="start"
                  />
                  <Tab 
                    label="Revenue Trends" 
                    icon={<Timeline />}
                    iconPosition="start"
                  />
                </Tabs>
              </Paper>

              {/* Tab Content */}
              {tabValue === 0 && (
                <Box>
                  {/* Monthly Production Chart */}
                  <ChartCard 
                    title="Monthly Production Overview"
                    icon={<BarChart />}
                  >
                    <ProductionBarByMonth monthlyOrderData={productionData?.monthlyOrderData} />
                  </ChartCard>

                  <Grid container spacing={4}>
                    {/* Dealer Analytics */}
                    <Grid item xs={12} lg={8}>
                      <ChartCard 
                        title="Dealer-wise Orders and Product Count"
                        icon={<BarChart />}
                      >
                        <Box height="400px">
                          <Bar data={dealerWiseData} options={chartOptions} />
                        </Box>
                      </ChartCard>
                    </Grid>

                    {/* Product Type Analytics */}
                    <Grid item xs={12} lg={4}>
                      <ChartCard 
                        title="Product Type Distribution"
                        icon={<PieChart />}
                      >
                        <Box height="400px">
                          <Doughnut data={productOrderCountData} options={doughnutOptions} />
                        </Box>
                      </ChartCard>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tabValue === 1 && (
                <Box>
                  <Grid container spacing={4}>
                    {/* Order Status */}
                    <Grid item xs={12} lg={6}>
                      <ChartCard 
                        title="Order Status Distribution"
                        icon={<PieChart />}
                      >
                        <Box height="400px">
                          <Doughnut data={orderStatusData} options={doughnutOptions} />
                        </Box>
                      </ChartCard>
                    </Grid>

                    {/* Revenue Distribution */}
                    <Grid item xs={12} lg={6}>
                      <ChartCard 
                        title="Revenue Distribution by Dealer"
                        icon={<BarChart />}
                      >
                        <Box height="400px">
                          <Bar 
                            data={revenueDistributionData} 
                            options={{
                              ...chartOptions,
                              scales: {
                                ...chartOptions.scales,
                                y: {
                                  ...chartOptions.scales.y,
                                  beginAtZero: true,
                                  title: {
                                    display: true,
                                    text: 'Revenue ($)',
                                    color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333'
                                  },
                                },
                              },
                            }} 
                          />
                        </Box>
                      </ChartCard>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  <ChartCard 
                    title="Dealer Revenue Trends Over Time"
                    icon={<Timeline />}
                  >
                    <Box height="500px">
                      <Line 
                        data={dealerRevenueTrendsData} 
                        options={{
                          ...chartOptions,
                          scales: {
                            ...chartOptions.scales,
                            y: {
                              ...chartOptions.scales.y,
                              beginAtZero: true,
                              title: {
                                display: true,
                                text: 'Revenue ($)',
                                color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333'
                              },
                            },
                            x: {
                              ...chartOptions.scales.x,
                              title: {
                                display: true,
                                text: 'Month',
                                color: theme.palette.mode === 'dark' ? '#ffffff' : '#333333'
                              },
                            },
                          },
                        }} 
                      />
                    </Box>
                  </ChartCard>
                </Box>
              )}
            </>
          )}
        </Container>
      </main>
    </div>
  );
};

export default ProductionAnalytics;